import {
  BookOpen,
  Building2,
  BadgeCheck,
  ChevronDown,
  ChevronRight,
  CloudRain,
  Flag,
  FlaskConical,
  History,
  LogOut,
  Map,
  MoreHorizontal,
  Radio,
  ShieldAlert,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { BattleReportDialog } from "./BattleReportDialog";
import { BunkerView } from "./BunkerView";
import { CommunityEndedDialog } from "./CommunityEndedDialog";
import { CrisisDialog } from "./CrisisDialog";
import { DefencePanel } from "./DefencePanel";
import { EvacuationDialog } from "./EvacuationDialog";
import { JournalPanel } from "./JournalPanel";
import { LegacySecuredDialog } from "./LegacySecuredDialog";
import { PeoplePanel } from "./PeoplePanel";
import { ResourceBar } from "./ResourceBar";
import { ResearchPanel } from "./ResearchPanel";
import { ResponsiveSheet } from "./ResponsiveSheet";
import { SpaceInspector } from "./SpaceInspector";
import { SurfaceMap } from "./SurfaceMap";
import { SurvivorStrip } from "./SurvivorStrip";
import { SoundToggle } from "./SoundToggle";
import { formatDurationRange, formatGameTime, formatQuantity } from "../lib/format";
import { usePresentationState } from "../lib/presentationClock";
import { useResponsiveLayout } from "../lib/useResponsiveLayout";
import type { BattleReport, CampaignPayload, GameCommand, GameState } from "../types";

type Tab = "bunker" | "surface" | "people" | "research" | "defence" | "journal";

const tabs = [
  { id: "bunker", label: "Bunker", icon: Building2 },
  { id: "surface", label: "Surface", icon: Map },
  { id: "people", label: "People", icon: Users },
  { id: "research", label: "Research", icon: FlaskConical },
  { id: "defence", label: "Defence", icon: ShieldAlert },
  { id: "journal", label: "Journal", icon: BookOpen },
] satisfies Array<{ id: Tab; label: string; icon: typeof Building2 }>;

const LEGACY_DISMISSAL_KEY = "bunkerfall:legacy-dialog-dismissed";
const BUNKER_HUD_KEY = "bunkerfall:bunker-hud:v1";

interface BunkerHudState {
  cameraOpen: boolean;
  historyOpen: boolean;
  manuallyChanged: boolean;
  objectiveOpen: boolean;
}

function defaultHudState(compact: boolean): BunkerHudState {
  return {
    cameraOpen: !compact,
    historyOpen: false,
    manuallyChanged: false,
    objectiveOpen: !compact,
  };
}

function readBunkerHudState(compact: boolean): BunkerHudState {
  if (typeof window === "undefined") return defaultHudState(compact);
  try {
    const raw = window.sessionStorage.getItem(BUNKER_HUD_KEY);
    if (!raw) return defaultHudState(compact);
    const saved = JSON.parse(raw) as Partial<BunkerHudState>;
    if (!saved.manuallyChanged) return defaultHudState(compact);
    return {
      cameraOpen: Boolean(saved.cameraOpen),
      historyOpen: Boolean(saved.historyOpen),
      manuallyChanged: true,
      objectiveOpen: Boolean(saved.objectiveOpen),
    };
  } catch {
    return defaultHudState(compact);
  }
}

export function readLegacyDialogDismissal(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(LEGACY_DISMISSAL_KEY);
  } catch {
    return null;
  }
}

export function writeLegacyDialogDismissal(outcomeKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LEGACY_DISMISSAL_KEY, outcomeKey);
  } catch {
    // The in-memory dismissal still applies when private/blocked storage fails.
  }
}

interface GameShellProps {
  payload: CampaignPayload;
  busy: boolean;
  error: Error | null;
  report: BattleReport | null;
  onCommand: (command: GameCommand) => Promise<unknown>;
  onDecision: (decision: "accept" | "take_command") => Promise<unknown>;
  onDismissReport: () => void;
  onReset: () => void;
}

export function MilestoneObjectiveBanner({
  title,
  description,
  isMilestone,
  completed,
  total,
  activeWorkOrders,
  expanded = true,
  onToggle,
}: {
  title: string;
  description?: string;
  isMilestone: boolean;
  completed: number;
  total: number;
  activeWorkOrders: number;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const completedForProgress = Math.min(completed, total);
  const currentMilestone = Math.min(completedForProgress + 1, total);
  const workOrderCopy = activeWorkOrders
    ? `${activeWorkOrders} work order${activeWorkOrders === 1 ? "" : "s"} active`
    : "No work underway";

  return (
    <section
      className={`objective-banner ${expanded ? "expanded" : "collapsed"}`}
      aria-labelledby="objective-banner-title"
      aria-describedby={expanded && description ? "objective-banner-description" : undefined}
      aria-live="polite"
      aria-atomic="true"
    >
      {onToggle && (
        <button
          aria-controls="objective-banner-details"
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse objective details" : "Expand objective details"}
          className="objective-banner-toggle"
          onClick={onToggle}
          type="button"
        >
          <ChevronDown aria-hidden="true" />
        </button>
      )}
      <span className="objective-banner-icon" aria-hidden="true">
        <Flag />
      </span>
      <div className="objective-banner-copy">
        <div className="objective-banner-kicker">
          <span className="eyebrow">
            {isMilestone ? "Next community milestone" : "Immediate priority"}
          </span>
          {isMilestone && total > 0 && (
            <small>
              Milestone {currentMilestone} of {total}
            </small>
          )}
        </div>
        <h2 id="objective-banner-title">{title}</h2>
        {expanded && description && (
          <p id="objective-banner-description">{description}</p>
        )}
      </div>
      {expanded && total > 0 && (
        <div className="objective-banner-progress" id="objective-banner-details">
          <span>
            <small>Community record</small>
            <strong>
              {completedForProgress}/{total}
            </strong>
          </span>
          <progress
            max={total}
            value={completedForProgress}
            aria-label={`${completedForProgress} of ${total} community milestones complete`}
          />
        </div>
      )}
      {expanded && (
        <div className="objective-work-orders" id={total > 0 ? undefined : "objective-banner-details"}>
          <ChevronRight aria-hidden="true" />
          <span>{workOrderCopy}</span>
        </div>
      )}
    </section>
  );
}

function SiteHistoryPanel({
  site,
  expanded,
  onToggle,
}: {
  site: GameState["site"];
  expanded: boolean;
  onToggle: () => void;
}) {
  const history = site.history;
  if (!history) return null;

  return (
    <aside className={`site-history-imprint ${expanded ? "expanded" : "collapsed"}`} aria-label={`Site history: ${history.name}`}>
      <button
        aria-controls="site-history-details"
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse inherited evidence" : "Expand inherited evidence"}
        className="site-history-toggle"
        onClick={onToggle}
        type="button"
      >
        <History aria-hidden="true" size={14} />
        <span><small>Inherited evidence</small><strong>{history.name}</strong></span>
        <ChevronDown aria-hidden="true" size={14} />
      </button>
      {expanded && (
        <div className="site-history-details" id="site-history-details">
          <p>{history.effect ?? history.summary}</p>
          <div className="site-effect-strips">
            {site.layout_effect && <span><b>{site.layout_effect.name}</b><small>{site.layout_effect.effect}</small></span>}
            <span><b>{site.geology.name}</b><small>{site.geology.effect ?? site.geology.summary}</small></span>
          </div>
        </div>
      )}
    </aside>
  );
}

export function GameShell({ payload, busy, error, report, onCommand, onDecision, onDismissReport, onReset }: GameShellProps) {
  const { catalog, campaign } = payload;
  const state = usePresentationState(payload, busy);
  const layout = useResponsiveLayout();
  const [hud, setHud] = useState<BunkerHudState>(() => readBunkerHudState(layout.compact));
  const [moreOpen, setMoreOpen] = useState(false);
  const [showConnection, setShowConnection] = useState(true);
  const [tab, setTab] = useState<Tab>("bunker");
  const [selectedSpaceId, setSelectedSpaceId] = useState("space-generator");
  const [selectedSurvivor, setSelectedSurvivor] = useState<string | null>(null);
  const outcomeKey = state.campaign_outcome?.status === "legacy_secured"
    ? `${campaign.id}:${state.campaign_outcome.achieved_at ?? "secured"}`
    : null;
  const [dismissedOutcomeKey, setDismissedOutcomeKey] = useState<string | null>(readLegacyDialogDismissal);
  const selectedSpace = useMemo(() => state.site.spaces.find((item) => item.id === selectedSpaceId) ?? state.site.spaces[0], [selectedSpaceId, state.site.spaces]);
  const completedMilestones = state.milestones?.completed ?? [];
  const milestoneEntries = Object.entries(catalog.milestones ?? {}).sort(
    ([, left], [, right]) => left.order - right.order,
  );
  const nextMilestone = milestoneEntries.find(
    ([id]) => !completedMilestones.includes(id),
  )?.[1];
  const tutorialObjective = state.tutorial.step === "repair_power"
    ? "Restore stable power"
    : state.tutorial.step === "staff_power"
      ? "Staff the generator"
    : state.tutorial.step === "excavate"
      ? "Open a chamber"
      : state.tutorial.step === "build_room"
        ? "Build a working room"
        : state.tutorial.step === "scavenge"
          ? "Send a scavenging party"
          : "Prepare for the horde";
  const tutorialActive = ["repair_power", "staff_power", "excavate", "build_room", "scavenge"].includes(
    state.tutorial.step,
  );
  const objectiveName = tutorialActive
    ? tutorialObjective
    : nextMilestone?.name ?? tutorialObjective;
  useEffect(() => {
    if (state.active_crisis || payload.crisis) {
      setTab("bunker");
      setMoreOpen(false);
    }
  }, [state.active_crisis, payload.crisis]);

  useEffect(() => {
    setHud((current) => current.manuallyChanged ? current : defaultHudState(layout.compact));
    if (!layout.narrow) setMoreOpen(false);
  }, [layout.compact, layout.narrow]);

  useEffect(() => {
    if (!hud.manuallyChanged) return;
    try {
      window.sessionStorage.setItem(BUNKER_HUD_KEY, JSON.stringify(hud));
    } catch {
      // In-memory HUD controls remain usable when storage is unavailable.
    }
  }, [hud]);

  useEffect(() => {
    setShowConnection(true);
    const timeout = window.setTimeout(() => setShowConnection(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [campaign.revision]);

  function toggleHud(panel: "cameraOpen" | "historyOpen" | "objectiveOpen") {
    setHud((current) => {
      const opening = !current[panel];
      const next = { ...current, [panel]: opening, manuallyChanged: true };
      if (layout.compact && opening && panel === "historyOpen") next.objectiveOpen = false;
      if (layout.compact && opening && panel === "objectiveOpen") next.historyOpen = false;
      return next;
    });
  }

  function dismissLegacyDialog() {
    if (!outcomeKey) return;
    setDismissedOutcomeKey(outcomeKey);
    writeLegacyDialogDismissal(outcomeKey);
  }

  function confirmCommunitySelector() {
    if (
      window.confirm(
        "Return to the community selector? Existing communities and cloud saves will remain available.",
      )
    ) {
      onReset();
    }
  }

  const visibleTabs = layout.narrow ? tabs.filter(({ id }) => id !== "journal") : tabs;
  const soundDanger = Boolean(payload.crisis || state.active_crisis || state.threat?.eta_range_seconds[0] === 0);

  return (
    <div className="game-shell">
      <header className="game-header">
        <div className="compact-brand"><span>BF</span><div><strong>BUNKERFALL</strong><small>{campaign.name}</small></div></div>
        <div className="site-readout">
          <span className={`power-dot power-${state.site.power}`} title={`${state.site.power} power`}><Zap size={11} /></span>
          <div>
            <strong>{state.site.name}</strong>
            <small>{state.site.archetype.name} · {state.site.geology.name}</small>
            {state.site.power_grid && (
              <small className="power-grid-summary">
                {state.site.power_grid.served}/{state.site.power_grid.capacity} load · {formatQuantity(state.site.power_grid.fuel_per_hour)} fuel/h
              </small>
            )}
          </div>
        </div>
        <ResourceBar state={state} catalog={catalog} />
        <div className="time-readout"><CloudRain size={15} /><span><strong>{formatGameTime(state.game_time)}</strong><small>Persistent time · autosaved</small></span></div>
      </header>

      <nav className={`game-nav ${layout.narrow ? "compact-nav" : ""}`} aria-label="Game sections">
        {visibleTabs.map(({ id, label, icon: Icon }) => <button key={id} aria-current={tab === id ? "page" : undefined} className={tab === id ? "active" : ""} onClick={() => setTab(id)}><Icon size={16} /><span>{label}</span>{id === "defence" && state.threat && <i>{formatDurationRange(state.threat.eta_range_seconds)}</i>}{id === "surface" && state.expeditions.some((item) => item.phase !== "complete") && <b />}</button>)}
        <span className="nav-spacer" />
        {outcomeKey && !layout.narrow && (
          <button
            className="legacy-nav-button"
            type="button"
            onClick={() => setDismissedOutcomeKey(null)}
            aria-label="Review secured community legacy"
          >
            <BadgeCheck aria-hidden="true" />
            <span>Legacy secured</span>
          </button>
        )}
        {!layout.narrow && <SoundToggle power={state.site.power} danger={soundDanger} />}
        {!layout.narrow && <button className="leave-campaign" onClick={confirmCommunitySelector} title="Choose or start a community" aria-label="Choose or start a community"><LogOut size={15} /><span>New game</span></button>}
        {layout.narrow && (
          <button
            aria-controls="game-more-sheet"
            aria-current={tab === "journal" ? "page" : undefined}
            aria-expanded={moreOpen}
            aria-label="More game options"
            className={tab === "journal" ? "active more-nav-button" : "more-nav-button"}
            onClick={() => setMoreOpen(true)}
            type="button"
          >
            <MoreHorizontal aria-hidden="true" size={16} />
            <span>More</span>
          </button>
        )}
      </nav>

      <ResponsiveSheet id="game-more-sheet" onClose={() => setMoreOpen(false)} open={moreOpen} title="More">
        <div className="more-menu-actions">
          <button className={tab === "journal" ? "active" : ""} onClick={() => { setTab("journal"); setMoreOpen(false); }} type="button">
            <BookOpen aria-hidden="true" /><span><strong>Journal</strong><small>Community record</small></span>
          </button>
          {outcomeKey && (
            <button onClick={() => { setDismissedOutcomeKey(null); setMoreOpen(false); }} type="button">
              <BadgeCheck aria-hidden="true" /><span><strong>Legacy secured</strong><small>Review this community's legacy</small></span>
            </button>
          )}
          <SoundToggle power={state.site.power} danger={soundDanger} />
          <button onClick={() => { setMoreOpen(false); confirmCommunitySelector(); }} type="button">
            <LogOut aria-hidden="true" /><span><strong>New game</strong><small>Choose or start a community</small></span>
          </button>
        </div>
      </ResponsiveSheet>

      <main className="game-main">
        {tab === "bunker" && selectedSpace && (
          <div className="bunker-workspace">
            <div className="bunker-stage-wrap">
              <div className={`bunker-context-hud ${layout.compact ? "compact" : "spacious"}`}>
                <MilestoneObjectiveBanner
                  title={objectiveName}
                  description={tutorialActive ? undefined : nextMilestone?.description}
                  isMilestone={!tutorialActive && Boolean(nextMilestone)}
                  completed={completedMilestones.length}
                  total={milestoneEntries.length}
                  activeWorkOrders={state.tasks.length}
                  expanded={hud.objectiveOpen}
                  onToggle={() => toggleHud("objectiveOpen")}
                />
                <SiteHistoryPanel
                  site={state.site}
                  expanded={hud.historyOpen}
                  onToggle={() => toggleHud("historyOpen")}
                />
              </div>
              <BunkerView
                state={state}
                catalog={catalog}
                selectedSpaceId={selectedSpace.id}
                onSelectSpace={setSelectedSpaceId}
                compact={layout.compact}
                controlsOpen={hud.cameraOpen}
                onToggleControls={() => toggleHud("cameraOpen")}
              />
            </div>
            <SpaceInspector
              state={state}
              catalog={catalog}
              space={selectedSpace}
              busy={busy}
              onCommand={onCommand}
            />
            <SurvivorStrip
              state={state}
              selected={selectedSurvivor}
              onSelect={(id) => {
                setSelectedSurvivor(id);
                setTab("people");
              }}
            />
          </div>
        )}
        {tab === "surface" && <SurfaceMap state={state} catalog={catalog} busy={busy} onCommand={onCommand} />}
        {tab === "people" && (
          <PeoplePanel
            state={state}
            catalog={catalog}
            selectedId={selectedSurvivor}
            busy={busy}
            onSelect={setSelectedSurvivor}
            onCommand={onCommand}
            onOpenSurface={() => setTab("surface")}
            onOpenInfirmary={(spaceId) => {
              setSelectedSpaceId(spaceId);
              setTab("bunker");
            }}
          />
        )}
        {tab === "research" && (
          <ResearchPanel
            state={state}
            catalog={catalog}
            busy={busy}
            onCommand={onCommand}
            onOpenGenerator={() => {
              const generators = state.site.spaces.filter(
                (space) => space.room?.type === "generator",
              );
              const generator = generators.find(
                (space) =>
                  space.room?.status === "operational"
                  && space.room.enabled !== false,
              ) ?? generators.find(
                (space) => space.room?.status === "operational",
              ) ?? generators[0];
              if (generator) setSelectedSpaceId(generator.id);
              setTab("bunker");
            }}
          />
        )}
        {tab === "defence" && <DefencePanel state={state} busy={busy} onCommand={onCommand} />}
        {tab === "journal" && <JournalPanel state={state} />}
      </main>

      {(busy || showConnection) && <div className="connection-state">{busy ? <><i />Saving orders…<span className="visually-hidden" role="status">Saving orders</span></> : <><Radio size={12} />Link stable · revision {campaign.revision}</>}</div>}
      {error && <div className="error-toast" role="alert"><ShieldAlert size={16} /><span>{error.message}</span></div>}
      {!report && (payload.crisis || state.active_crisis) && <CrisisDialog payload={payload} busy={busy} onDecision={onDecision} onCommand={onCommand} />}
      {report && <BattleReportDialog report={report} state={state} onClose={onDismissReport} />}
      {state.community_status === "evacuating" && !report && <EvacuationDialog state={state} busy={busy} onCommand={onCommand} />}
      {state.community_status === "ended" && !report && <CommunityEndedDialog state={state} onReturnToTitle={onReset} />}
      {outcomeKey && dismissedOutcomeKey !== outcomeKey && !report && !payload.crisis && !state.active_crisis && (
        <LegacySecuredDialog
          state={state}
          onContinue={dismissLegacyDialog}
          onReviewHistory={() => {
            dismissLegacyDialog();
            setTab("journal");
          }}
        />
      )}
    </div>
  );
}
